<svelte:options tag="scotparl-player" />

<script context="module">
    getExtendedLocalStorage();
</script>

<script lang="ts">
    import { onDestroy, onMount, setContext, tick } from "svelte";
    import type { Writable } from "svelte/store";
    import { writable } from "svelte/store";

    import type { IVuPlayer } from "../../../modules/vu-player-models/player/IVuPlayer";
    import type { SkinState, State } from "./types/state";
    import { loadShaka } from "../../../modules/shaka-3/ShakaSDKLoader";
    import { loadSDK } from "../../../modules/vu-core/network/sdk-loader";
    import { ErrorsLogger } from "./utils/errors-logger";
    import { PlayerAdapter } from "./adapter";
    import { SupportedEvents } from "./../../../modules/vu-events/SupportedEvents";
    import { getStreamType } from "./../../../modules/vu-core/stream/get-stream-type";
    import { delay } from "./../../../modules/vu-core/utils/delay";
    import { getExtendedLocalStorage } from "./../../../modules/vu-core/storage/get-extended-local-storage";

    import Skin from "./components/Skin.svelte";
    import { isAttributeTrue } from "./utils/utils";
    import { localStorageKeys } from "./utils/constants";
    import type { SkinConfig } from "./types/skin-config";
    import { UTCToHHMMSS } from "../../../modules/vu-core/date-time/utc-to-hhmmss";
    import { toHHMMSS } from "../../../modules/vu-core/date-time/to-hhmmss";

    const shakaLibUrl = "/lib/shaka/shaka-player.scotparl.js";

    let errorHandler;

    let showInfoBox = writable(false);
    let state: Writable<State> = writable({
        hasCanplayFired: false,
        playerState: "uninitialised",
        isAbrEnabled: true,
        isLive: false,
        isMuted: false,
        volume: 1,

        errors: [],

        videoTracks: [],
        audioTracks: [],
        textTracks: [],
    });
    let skinState: Writable<SkinState> = writable({
        defaultFrameRate: 25,
        showInfoBox,
        showClipControls: false,
        showHiddenMenu: false,

        playerWidth: 0,
        playerHeight: 0,

        isFullscreen: false,
        isSettingsOpen: false,
        isOverlayHidden: false,
        isSubMenuOpen: false,
        isShiftKeyPressed: false,

        setFocusOnSettingsButton: () => {},

        liveTimeFormatter: UTCToHHMMSS,
        vodCurrentTimeFormatter: toHHMMSS,
        vodDurationFormatter: toHHMMSS,
    });
    let videoContainer;

    export let adapter: IVuPlayer;

    export let info: string | boolean = localStorage.getPrimitiveItemOrDefault(
        localStorageKeys.showInfoBox,
        false
    );
    export let defaultFrameRate = 25;
    export let clip: string | boolean = false;
    export let muted: string | boolean = false;

    setContext("playerState", state);
    setContext("skinState", skinState);

    onMount(async () => {
        await loadSDK(shakaLibUrl, "shaka");
        //await loadShaka(shakaVersion);
        await tick();

        // TODO: find a better solution
        await delay(100);
        updateAttributes();
    });

    onDestroy(() => {
        errorHandler.unbind();
    });

    function onCanPlay() {
        $state.hasCanplayFired = true;

        updateTracks();
        onTimeUpdate();
    }

    function onStateChange() {
        $state.playerState = adapter.state;

        updateTracks();
    }

    function onTimeUpdate() {
        updateTimes();
    }

    function onSourceLoaded() {
        $state.sourceType = getStreamType(adapter.src);
        $state.hasCanplayFired = true;

        updateTracks();
        updateTimes();
    }

    function onVolumeChange() {
        $state.isMuted = adapter.isMuted;
        $state.volume = adapter.isMuted ? 0 : adapter.volume;
    }

    function onError(error) {
        $state.errors = [...$state.errors, error];
    }

    function updateTimes() {
        $state.currentUTC = adapter.currentUTC;
        $state.currentTime = adapter.currentTime;
        $state.dvr = adapter.dvr;
        $state.duration = adapter.duration;
        $state.seekableRange = adapter.seekableRange;
    }

    function onProgress() {
        // Good enough to update viedo quality every n seconds
        updateTracks();
    }

    function updateTracks() {
        $state.activeVideoTrack = adapter.activeVideoTrack;
        $state.activeAudioTrack = adapter.activeAudioTrack;
        $state.activeTextTrack = adapter.activeTextTrack;

        $state.videoTracks = adapter.videoTracks;
        $state.audioTracks = adapter.audioTracks;
        $state.textTracks = adapter.textTracks;

        $state.isAbrEnabled = adapter.abr ?? true;
    }

    function updateAttributes() {
        $skinState.defaultFrameRate = defaultFrameRate ?? 25;
        $skinState.showClipControls = isAttributeTrue(clip);

        $showInfoBox = isAttributeTrue(info);
        showInfoBox.subscribe(storeShowInfoBox);
    }

    function storeShowInfoBox(value) {
        localStorage.setPrimitiveItem(localStorageKeys.showInfoBox, value);
    }

    function subscribe() {
        adapter.on(SupportedEvents.CanPlay, onCanPlay);
        adapter.on(SupportedEvents.StateChange, onStateChange);
        adapter.on(SupportedEvents.VideoTrackChange, updateTracks);
        adapter.on(SupportedEvents.AudioTrackChange, updateTracks);
        adapter.on(SupportedEvents.AudioTrackAdded, updateTracks);
        adapter.on(SupportedEvents.TextTrackChange, updateTracks);
        adapter.on(SupportedEvents.TimeUpdate, onTimeUpdate);
        adapter.on(SupportedEvents.SourceLoaded, onSourceLoaded);
        adapter.on(SupportedEvents.VolumeChange, onVolumeChange);
        adapter.on(SupportedEvents.Progress, onProgress);

        errorHandler = new ErrorsLogger(onError, adapter);
    }

    function setInitialSkinState() {
        isAttributeTrue(muted) ? adapter.mute() : adapter.unmute();
    }

    export async function setSkin(skinConfig: SkinConfig) {
        if (skinConfig.liveTimeFormatter) {
            $skinState.liveTimeFormatter = skinConfig.liveTimeFormatter;
        }

        if (skinConfig.vodCurrentTimeFormatter) {
            $skinState.vodCurrentTimeFormatter =
                skinConfig.vodCurrentTimeFormatter;
        }

        if (skinConfig.vodDurationFormatter) {
            $skinState.vodDurationFormatter = skinConfig.vodDurationFormatter;
        }

        return Promise.resolve();
    }

    export async function init(playerConfig = {}, isLive = false) {
        //await loadShaka(shakaVersion);
        await loadSDK(shakaLibUrl, "shaka");

        $state.playerConfig = playerConfig;
        $state.isLive = isLive;
        $state.adapter = PlayerAdapter(
            {
                ...playerConfig,
                container: videoContainer,
            },
            isLive
        );

        adapter = $state.adapter as IVuPlayer;

        subscribe();

        return Promise.resolve();
    }

    export async function load(sourceConfig) {
        $state.sourceConfig = sourceConfig;
        return $state.adapter?.load(sourceConfig).then(setInitialSkinState);
    }
</script>

<Skin>
    <div class="video-container" bind:this={videoContainer}>
        <div class="shaka-text-container" style="display: none;"><div><span></span></div></div>
    </div>
</Skin>

<style lang="scss">
    .video-container {
        display: flex;
        justify-content: center;
        align-items: center;
        height: 100%;
        background-color: black;
    }

    .shaka-text-container > div  {
        background: rgba(0,0,0,0.5) !important;
        text-align: center !important;
        justify-content: center !important;
        height: auto !important;
        padding: 0.5em !important;
        margin: -0.5em !important;

        > span {
            font-size: 1em !important;
        }
    }

    /* Hack for Safari to place subtitles higher up so they dont cover astons */
    ::-webkit-media-text-track-display-backdrop {
        top:-5em;
        position: relative;
    }

</style>
